import { Translation } from '@ps/web-shared-components';
import type { GridMarketTemplateConfig } from '@ps/web-sports-api-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type { Participants } from '@swf/sports/types/event';
import type {
  MarketTemplateComponent,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';

import SelectionTable from '../../components/SelectionTable/SelectionTable';
import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';
import transformRunnersToRows from '../../utils/transformRunnersToRows';

import { TXTWCL_Away, TXTWCL_Draw, TXTWCL_Home_8, TXTWCL_Show_More } from './Grid.messages.json';
import styles from './Grid.scss';

const SHOW_MORE_THRESHOLD = 6;

const useColumnTitles = (participants: Participants | undefined, numberOfColumns = 3): string[] => {
  const { formatMessage } = useRegulatoryIntl();

  if (!participants) {
    if (numberOfColumns === 2) {
      return ['1', '2'];
    }

    return [formatMessage(TXTWCL_Home_8), formatMessage(TXTWCL_Draw), formatMessage(TXTWCL_Away)];
  }

  if (numberOfColumns === 2) {
    return participants;
  }

  return [participants[0], formatMessage(TXTWCL_Draw), participants[1]];
};

export const Grid: MarketTemplateComponent<SingleMarketTemplateProps> = ({
  marketId,
  templateConfig
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const participants = useSelector((state: RootState) =>
    widgetSelect.participants(state, market?.eventId ?? -1)
  );

  const { eventStartTime } = useSelector((state: RootState) =>
    widgetSelect.event(state, market?.eventId ?? 0)
  );

  const config = templateConfig as GridMarketTemplateConfig;

  const runners = market
    ? market.runners.filter((r) => config.runners.includes(r.selectionId))
    : [];
  const anyOtherScoresRunner = market
    ? market.runners.find((r) => config.anyOtherScoresRunners.includes(r.selectionId))
    : undefined;

  const rows = transformRunnersToRows(runners, participants);
  const headerRow = useColumnTitles(participants, rows[0]?.length);

  const [rowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    rows,
    marketId,
    SHOW_MORE_THRESHOLD
  );

  if (!market) return null;

  return (
    <>
      <SelectionTable
        header={headerRow}
        rows={rowsToDisplay}
        market={market}
        showScore
        testId="market-template-grid"
        eventStartTime={eventStartTime}
        allOtherScoresRunner={anyOtherScoresRunner}
        hasLimitedRows={limitRows}
      />
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

Grid.marketTemplateType = 'EVENT';

export default Grid;
